import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { ClassroomSummaryService } from '../../classroom360/classroom-summary/classroom-summary.service';
import { Classroom360Service } from '../../classroom360/classroom360.service';
import { DataQueryService } from '../../classroom360/data-query/data-query.service';
import { GradebookAccessDeniedComponent } from '../../classroom360/gradebook/v9/modals/gradebook-access-denied/gradebook-access-denied.component';
import { SelectCourseDialogComponent } from '../../classroom360/guards/select-course/select-course-dialog/select-course-dialog.component';
import { StaffService } from '../../management360/staff/staff.service';
import { LoginService } from '../../shared-modules/login/login.service';
import { MyReportsComponent } from '../../shared-modules/reporting/my-reports/my-reports.component';
import { ReportFoldersComponent } from '../../shared-modules/reporting/report-folders/report-folders.component';
import { ReportSnapshotsComponent } from '../../shared-modules/reporting/report-snapshots/report-snapshots.component';
import { ReportingComponent } from '../../shared-modules/reporting/reporting.component';
import { ReportingService } from '../../shared-modules/reporting/reporting.service';
import { OnlineRegistrationService } from '../../student360/online-registration/online-registration.service';
import { MultipleFamiliesComponent } from '../../student360/online-registration/student-update/multiple-families/multiple-families.component';
import { Student360Service } from '../../student360/student360.service';
import { GeneralConfirmationComponent } from '../controls/general-confirmation/general-confirmation.component';
import { EFormService } from '../e-forms/e-form.service';
import { Portal } from '../portal.model';
import { PermissionEnum } from '../security/permissions.enums';
import { SecurityService } from '../security/security.service';
import { SharedService } from '../shared.service';
import { LoaderService } from '../spinner/loader-service.component';
import { StaffSearchComponent } from '../staff-search/staff-search.component';
import { StudentSearchComponent } from '../student-search/student-search.component';
import { StudentService } from '../student/student.service';
import { PortalMenuItem, PortalMenuPreferences } from '../user-preferences/portal-menu-preferences.model';
import { UserPreferencesWindowComponent } from '../user-preferences/user-preferences-window/user-preferences-window.component';
import { UserPreferencesService } from '../user-preferences/user-preferences.service';
import { UserService } from '../user/user.service';
import { School } from '../year/school.model';
import { YearService } from '../year/year.service';

@Component({
    selector: 'menu',
    templateUrl: './menu.component.html',
    styleUrls: [
        './menu.component.css'
    ]
})

export class MenuComponent implements OnInit, OnDestroy {

    // Static Properties
    static menuByGroupName: { [key: string]: Portal[] } = {};
    static portalMenuPreferences: { [key: string]: PortalMenuPreferences } = {};

    // Interface Properties
    @Input() menuIdentifier = 'main';
    @Input() favoritesFilter: boolean;
    @Input() favoritesCheckbox: boolean;
    @Input() favoritesDefaults: boolean;
    @Output() toggleCloseEvent = new EventEmitter<any>();
    @Output() portalCheckboxEvent = new EventEmitter<any>();

    // Properties
    subscriptions: Subscription[] = [];
    portals: Portal[] = [];
    preferences: PortalMenuPreferences;
    userPrefDialogRef: MatDialogRef<UserPreferencesWindowComponent>;
    myReportsDialogRef: MatDialogRef<MyReportsComponent>;
    reportFoldersDialogRef: MatDialogRef<ReportFoldersComponent>;
    reportSnapshotsDialogRef: MatDialogRef<ReportSnapshotsComponent>;
    gradebookAccessDeniedDialog: MatDialogRef<GeneralConfirmationComponent>;
    v10: boolean;

    // Class Functions
    constructor(
        private classroom360Service: Classroom360Service,
        private userService: UserService,
        private student360Service: Student360Service,
        private userPreferencesService: UserPreferencesService,
        private loaderService: LoaderService,
        private studentService: StudentService,
        private dialog: MatDialog,
        private sharedService: SharedService,
        private dataQueryService: DataQueryService,
        private classroomSummaryService: ClassroomSummaryService,
        private onlineRegService: OnlineRegistrationService,
        private router: Router,
        private loginService: LoginService,
        private reportService: ReportingService,
        private reportSnapshots: ReportSnapshotsComponent,
        private yearService: YearService,
        private onlineRegistrationService: OnlineRegistrationService,
        private eFormService: EFormService,
        private securityService: SecurityService,
        private staffService: StaffService
        // private reportingComponent: ReportingComponent;

    ) { }

    get portalMenuPreferences(): PortalMenuPreferences {
        return MenuComponent.portalMenuPreferences[this.menuIdentifier];
    }

    set portalMenuPreferences(value: PortalMenuPreferences) {
        MenuComponent.portalMenuPreferences[this.menuIdentifier] = value;
    }

    get menuByGroupName(): Portal[] {
        return MenuComponent.menuByGroupName[this.menuIdentifier];
    }

    set menuByGroupName(value: Portal[]) {
        MenuComponent.menuByGroupName[this.menuIdentifier] = value;
    }

    // Event Functions
    ngOnInit() {
        this.v10 = this.loginService.LoginSettings.V10;
        // Setting the current and previous portals appropriately
        this.subscriptions.push(

            // Router events changed
            this.router.events.subscribe(event => {
                if (event instanceof NavigationEnd) {
                    const route = event.urlAfterRedirects;
                    const currentPortal = this.sharedService.currentPortal;
                    if (this.portals && (!currentPortal || !route.includes(currentPortal.ParentModule + currentPortal.PortalDescription))) {
                        const matchingPortals = this.portals.filter(portal => route.includes(portal.ParentModule + portal.PortalDescription));
                        let nextPortal: Portal;
                        if (matchingPortals.length == 1) {
                            nextPortal = matchingPortals[0];
                        } else if (matchingPortals.length > 1) {
                            const staffMatchingPortal = matchingPortals.find(portal => !portal.applyFilterStudents);
                            if (staffMatchingPortal) {
                                nextPortal = staffMatchingPortal;
                            } else {
                                nextPortal = matchingPortals.find(portal => portal.applyFilterStudents);
                            }
                        }
                        this.sharedService.currentPortal = nextPortal;
                    }
                }
            })
        );

        // Synchronizing portal changes
        this.subscriptions.push(
            this.classroom360Service.observableStaffAssignedPortalsStream$.subscribe(portals => {
                if (this.userService.userType == 1
                    && portals
                    && portals.length > 0
                    && (!this.menuByGroupName || this.menuByGroupName.length == 0)) {
                    this.portals = _.cloneDeep(portals);
                    if (this.portals) {
                        if (this.preferences) {
                            this.arrangeMenus();
                        } else {
                            this.subscriptions.push(
                                this.userPreferencesService.getStaffMenuPreferences().subscribe(preferences => {
                                    this.preferences = JSON.parse(preferences[0]);
                                    this.arrangeMenus();
                                })
                            );
                        }
                    }
                }
            }),
            this.student360Service.observableParentAssignedPortalsStream$.subscribe(portals => {
                if (this.userService.userType != null
                    && this.userService.userType != 1
                    && portals
                    && portals.length > 0
                    && (!this.menuByGroupName || this.menuByGroupName.length == 0)) {
                    this.portals = _.cloneDeep(portals);
                    if (this.portals) {
                        if (this.preferences) {
                            this.arrangeMenus();
                        } else if (this.studentService.selectedStudent) {
                            this.subscriptions.push(
                                this.userPreferencesService.getStudentMenuPreferences(this.studentService.selectedStudent.Id).subscribe(preferences => {
                                    if (preferences && preferences.length > 0 && preferences[0] != '') {
                                        this.preferences = JSON.parse(preferences[0]);
                                    }
                                    this.arrangeMenus();
                                })
                            );
                        }
                    }
                }
            })
        );

        if (this.favoritesFilter) {
            this.menuIdentifier = 'favorites';
            this.arrangeMenus();
        }
        this.eFormService.eFormHeadLinePermission = false;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    menuPortalClick(portal: Portal) {
        // PRE: a portal is clicked
        // POST: routes to the portal
        this.toggleCloseEvent.emit();
        const selectedStudent = this.studentService.selectedStudent;
        const parentModule = portal.ParentModule ? portal.ParentModule : '/student360';
        const selectedStaff = this.staffService.selectedStaff;

        // If the Portal is Programs and Services -  Single Student then need a studentsearch popup
        if (portal.GroupName == 'Programs And Services' && portal.PortalName == 'Single Student') {
            portal.applyFilterStudents = true;
        }

        // If the Portal is Attendance - By Student then need a studentsearch popup
        if ((portal.GroupName == 'Attendance Administration' || portal.GroupName == 'Processing') && portal.PortalName == 'Attendance - By Student') {
            portal.applyFilterStudents = true;
        }

        if (portal.PortalName == 'Special Services Documents') {
            portal.applyFilterStudents = true;
        }

        if (portal.PortalDescription == '/scheduleHistory/multipleStudent') {
            portal.applyFilterStudents = false;
        }

        // TODO: WE SHOULD BE USING THE REQUIRESTUDENTGUARD. THIS IS DEPRECIATED.
        if ((!selectedStudent || selectedStudent.Id < 0)
            && portal.applyFilterStudents) {
            this.studentSearch(parentModule + portal.PortalDescription, portal);
        }
        else if (!this.v10 && (portal.PortalName == 'Online Registration' || portal.PortalName == 'Update Household Data')) {
            let isUHD = false;
            if (portal.PortalName == 'Update Household Data') {
                isUHD = true;
            }
            this.onlineRegService.getOnlineRegFamilies(isUHD).subscribe(data => {
                if (data.length > 1) {
                    const dialogBox = this.dialog.open(MultipleFamiliesComponent, {
                        panelClass: 'tyl-mat-dialog',
                        data: { families: data, isUHD: isUHD },
                        disableClose: true
                    });
                    const subscription = dialogBox.afterClosed().subscribe(result => {
                        subscription.unsubscribe();
                        if (parseInt(result, 0) > 0) {
                            this.onlineRegistrationService.isMultipleFamiliesPopupOpened = true;
                            this.router.navigateByUrl(parentModule + portal.PortalDescription);
                        }
                    });
                }
                else if (data.length == 1) {
                    this.onlineRegistrationService.isMultipleFamiliesPopupOpened = false;
                    this.onlineRegistrationService.familyId = data[0].FamilyId;
                    this.onlineRegistrationService.parentType = data[0].ParentType;
                    this.router.navigateByUrl(parentModule + portal.PortalDescription);
                }
            });

        }
        else if (portal.PortalName == 'Roster Query') {
            this.dataQueryService.rosterQuery = true;
            const dialogBox = this.dialog.open(SelectCourseDialogComponent, {
                disableClose: true,
                hasBackdrop: true,
                panelClass: 'tyl-mat-dialog'
            });
            const subscription = dialogBox.afterClosed().subscribe(course => {
                subscription.unsubscribe();
                if (course) {
                    this.classroomSummaryService.selectedCourse = course;
                    this.sharedService.currentPortal = portal;
                    this.router.navigateByUrl(parentModule + portal.PortalDescription);
                }
            });
        }
        // reports
        else if (portal.PortalName == 'Report Folders') {
            this.reportService.isReportsMenu = true;

            this.reportService.getMyReportFolders().subscribe(reportFolders => {
                this.reportService.ReportFolders = reportFolders;

                const dialogBox = this.dialog.open(ReportFoldersComponent, {
                    disableClose: true,
                    // hasBackdrop: false,
                    // panelClass: 'disableModalStyle'
                });
                const subscription = dialogBox.afterClosed().subscribe(course => {
                    subscription.unsubscribe();
                    return;
                });

                subscription.unsubscribe();
            });
        }
        else if (portal.PortalName == 'My Reports') {
            this.reportService.isReportsMenu = true;

            const dialogBox = this.dialog.open(ReportingComponent, {
                disableClose: true,
                // hasBackdrop: false,
                // panelClass: 'disableModalStyle'
            });
            const subscription = dialogBox.afterClosed().subscribe(course => {
                subscription.unsubscribe();
                return;

            });
        }
        else if (portal.PortalName == 'Report Snapshots') {
            this.reportService.isReportsMenu = true;

            this.reportService.getMyReportFolders().subscribe(reportFolders => {
                this.reportService.ReportFolders = reportFolders;
                // temp set selected report to first in array to load snaps
                this.reportService.SelectedReport = this.reportService.ReportFolders[0].Reports[0];
                // this.reportSnapshots.selectedFolderChanged();
                this.reportSnapshots.selectedFolderName = 'All';
                // this.reportSnapshots.selectedReport.Name = 'All';

                // this.reportSnapshots.selectedFolderName = 'All';
                // this.reportSnapshots.selectedReport.Name = 'All';

                const dialogBox = this.dialog.open(ReportSnapshotsComponent, {
                    disableClose: true,
                    // hasBackdrop: false,
                    // panelClass: 'disableModalStyle'
                });
                const subscription = dialogBox.afterClosed().subscribe(course => {
                    subscription.unsubscribe();
                    return;
                    // subscription.unsubscribe();
                });


            });
        }
        else if (parentModule == '/health360' && portal.PortalDescription.toLowerCase() == '/staffhealth' && !selectedStaff) {
            this.StaffSearch(parentModule + portal.PortalDescription, portal);
        }

        // All other portals
        else {
            this.dataQueryService.rosterQuery = false;
            let portalDescription = portal.PortalDescription;
            if (portal.PortalDescription == '/gradebook') {
                portalDescription = this.v10 ? portalDescription + '/V10' : portalDescription + '/V9';
                if (this.classroomSummaryService.selectedCourse && this.classroomSummaryService.selectedCourse.MergedForAttendance) {
                    this.gradebookAccessDeniedDialog = this.dialog.open(GeneralConfirmationComponent, { panelClass: 'tyl-mat-dialog', disableClose: true });
                    this.gradebookAccessDeniedDialog.componentInstance.disableConfirmation = true;
                    this.gradebookAccessDeniedDialog.componentInstance.headerLabel = 'Access Denied';
                    this.gradebookAccessDeniedDialog.componentInstance.confirmMessage = 'The gradebook cannot be opened for Merged Attendance Classes';
                    return;
                }
                else if (!this.v10 && this.classroomSummaryService.selectedCourse && (this.classroomSummaryService.selectedCourse.GradeScaleExists == false || this.classroomSummaryService.selectedCourse.GradebookAccess == false)) {
                    const gradebookAccessDeniedDialogRef = this.dialog.open(GradebookAccessDeniedComponent, {
                        panelClass: 'tyl-mat-dialog',
                        width: '500px',
                        disableClose: true
                    });
                    gradebookAccessDeniedDialogRef.componentInstance.course = this.classroomSummaryService.selectedCourse;
                    this.classroom360Service.gradebookPortalSelected = true;
                    return;
                }
                else {
                    this.classroom360Service.gradebookPortalSelected = true;
                }
            }
            else if (portal.PortalDescription == '/classroomAttendance' || portal.PortalDescription == '/seatingChart') {
                this.classroom360Service.attendancePortalSelected = true;
            }
            else if (portal.PortalDescription == '/classAnnouncements' || portal.PortalDescription == '/classInformation') {
                this.classroom360Service.courseInfoPortalSelected = true;
            }
            if (!this.v10 && portal != null && portal.ParentModule == '/student360' && selectedStudent != undefined && selectedStudent != null) {
                this.yearService.getUserYears(this.userService.userId, this.userService.userType, selectedStudent.Id).subscribe(years => {
                    this.yearService.loadYears(years);
                    this.yearService.years = years;
                });
            }
            this.sharedService.currentPortal = portal;
            this.router.navigateByUrl(parentModule + portalDescription);
        }
    }

    childMenuClick(portal: Portal) {
        this.menuPortalClick(portal);
    }

    portalCheckboxClick(portal: Portal) {
        setTimeout(() => {
            this.portalCheckboxEvent.emit(portal);
        }, 0);
    }

    childPortalCheckboxClick(portal: Portal) {
        setTimeout(() => {
            this.portalCheckboxEvent.emit(portal);
        }, 0);
    }

    refreshMenu() {
        this.classroom360Service.staffAssignedPortals.forEach(portal => {
            const index: number = this.userPreferencesService.favoritesPreferences.FavoritePortals.findIndex(favPortal =>
                favPortal.Id == portal.Id
                || ((favPortal.Id == null || favPortal.Id == 0)
                    && favPortal.GroupName == portal.GroupName
                    && favPortal.PortalDescription.split('?')[0] == portal.PortalDescription.split('?')[0]
                    && favPortal.PortalModule == portal.ParentModule
                    && favPortal.PortalName == portal.PortalName));

            portal.Selected = index != -1;
        });

        this.classroom360Service.updateStaffAssignedPortals(this.classroom360Service.staffAssignedPortals);
    }

    // Methods
    arrangeMenus() {
        // PRE: the portals and preferences are set
        // POST: arranges the portals according to the preferences

        // Sorting the portals
        this.portals.sort((a, b) =>
            a.PortalName.localeCompare(b.PortalName)
        );

        // Arranging the portals by group
        if (this.v10) {
            this.portalMenuPreferences = new PortalMenuPreferences();
            this.portalMenuPreferences.PortalMenus = [];
            this.menuByGroupName = [];
            if (this.userService.staffUserStudentLocked) {
                this.portals = this.portals.filter(p => p.ParentModule == '/student360');
            }
        }

        this.portals.filter(portal => portal.ShowAsMenu && !portal.Deleted && ((this.v10 && portal.IsFavorite) || (!this.v10 && portal.Selected) || !this.favoritesFilter)).forEach(portal => {
            // Determining if the portal is a student filtered portal
            // TODO: WE SHOULD BE USING THE REQUIRESTUDENTGUARD. THIS IS DEPRECIATED.
            if (portal.ParentPortal || portal.GroupName.includes('Student') || portal.PortalName == 'Academic History') {
                portal.applyFilterStudents = true;
                if (portal.PortalDescription != null && portal.PortalDescription != '' && !portal.PortalDescription.includes('?studentPortal=true')) {
                    portal.PortalDescription += '?studentPortal=true';
                }
            }

            // Determining if the portal is an admin portal
            if (portal.PortalName == 'Review Behavior Referrals' || portal.PortalName == 'Manage Behavior Referrals') {
                portal.PortalDescription += '?adminPortal=true';
            }

            if (this.v10) {
                // Setting the group up
                if (!this.menuByGroupName[portal.GroupName]) {
                    this.menuByGroupName[portal.GroupName] = [];
                }
                this.menuByGroupName[portal.GroupName].push(portal);

                // Setting the portal menu header up
                if (this.portalMenuPreferences.PortalMenus.filter(o => o.GroupName == portal.GroupName).length == 0) {
                    let expanded = 1;
                    if (this.preferences &&
                        this.preferences.PortalMenus &&
                        this.preferences.PortalMenus.filter(o => o.GroupName == portal.GroupName && o.Expanded == 0).length > 0) {
                        expanded = 0;
                    }
                    const portalMenu: PortalMenuItem = {
                        GroupName: portal.GroupName,
                        Expanded: expanded,
                        GroupIcon: portal.GroupIcon,
                        Id: portal.Id
                    };
                    this.portalMenuPreferences.PortalMenus.push(portalMenu);
                }
            }
            else if (portal.PortalDescription == '') {
                const portalMenuPreference = this.preferences.PortalMenus.filter(o => o.Id == portal.Id || ((o.Id == null || o.Id == 0) && o.GroupName == portal.GroupName))[0];
                portal.Expanded = portalMenuPreference == null || portalMenuPreference.Expanded == 1 ? 1 : 0;
            }
        });

        // Sorting the portal menus
        if (this.v10) {
            this.portalMenuPreferences.PortalMenus.sort((a, b) =>
                a.GroupName.localeCompare(b.GroupName)
            );
            if (this.userService.staffUserStudentLocked) {
                if (this.portalMenuPreferences.PortalMenus.filter(m => m.GroupName == 'My Students').length > 0) {
                    this.portalMenuPreferences.PortalMenus = this.portalMenuPreferences.PortalMenus.filter(m => m.GroupName == 'My Students');
                }
                else {
                    this.portalMenuPreferences.PortalMenus = this.portalMenuPreferences.PortalMenus.filter(m => m.GroupName == 'Students');
                }
            }
        }
        else {
            this.checkEformPermission();
        }
    }

    checkEformPermission() {
        if (!this.v10) {
            this.eFormService.eFormHeadLinePermission = false;
            if (this.portals.filter(o => o.PortalName.toLowerCase() == 'e-forms').length > 0) {
                this.eFormService.eFormHeadLinePermission = true;
            }
            this.eFormService.observableeFormHeadLinePermission.next();
        }
    }

    // TODO: WE SHOULD BE USING THE REQUIRESTUDENTGUARD. THIS IS DEPRECIATED.
    studentSearch(redirectUrl: string, portal: Portal) {
        switch (portal.PortalName) {
            case 'Academic History': {
                if (portal.ParentModule == '/student360') {
                    this.getStudentSearchSchools(
                        redirectUrl,
                        portal,
                        [
                            PermissionEnum.AcademicHistory
                        ]
                    );
                }
                else {
                    this.getStudentSearchSchools(
                        redirectUrl,
                        portal,
                        [
                            PermissionEnum.AcademicHistoryAddEdit,
                            PermissionEnum.AcademicHistoryView,
                            PermissionEnum.AcademicHistoryViewAddEdit
                        ]
                    );
                }
                break;
            }
            case 'Communication Log': {
                this.getStudentSearchSchools(
                    redirectUrl,
                    portal,
                    [
                        PermissionEnum.AddEditAllCommunicationLogsCounselor,
                        PermissionEnum.AddEditAllCommunicationLogsMyStudents,
                        PermissionEnum.AddEditAllCommunicationLogsStudentRecord,
                        PermissionEnum.AddEditMyCommunicationLogCounselor,
                        PermissionEnum.AddEditMyCommunicationLogMyStudents,
                        PermissionEnum.AddEditMyCommunicationLogStudentRecord,
                        PermissionEnum.ViewAllCommunicationLogsCounselor,
                        PermissionEnum.ViewAllCommunicationLogsMyStudents,
                        PermissionEnum.ViewAllCommunicationLogsStudentRecord,
                        PermissionEnum.ViewMyCommunicationLogCounselor,
                        PermissionEnum.ViewMyCommunicationLogMyStudents,
                        PermissionEnum.ViewMyCommunicationLogStudentRecord,
                        PermissionEnum.ViewCommunicationLogsCounselorFunctions
                    ]
                );
                break;
            }
            case 'Student Notes': {
                this.getStudentSearchSchools(
                    redirectUrl,
                    portal,
                    [
                        PermissionEnum.StudentNotesCounselorAddEdit,
                        PermissionEnum.StudentNotesCounselorView,
                        PermissionEnum.StudentNotesMyStudentsAddEdit,
                        PermissionEnum.StudentNotesMyStudentsView,
                        PermissionEnum.StudentNotesStudentRecordAddEdit,
                        PermissionEnum.StudentNotesStudentRecordView
                    ]
                );
                break;
            }
            case 'Behavior Referral': {
                this.getStudentSearchSchools(
                    redirectUrl,
                    portal,
                    [
                        PermissionEnum.AddEditDisciplineReferral,
                        PermissionEnum.AllowEditDeleteReferral,
                        PermissionEnum.ViewDisciplineReferral,
                        PermissionEnum.AcceptDisciplineReferrals,
                        PermissionEnum.ViewAllDisciplineReferrals
                    ]
                );
                break;
            }
            default: {
                if (this.v10 && portal.ParentModule == '/student360' && portal.RequiredPrivilegeIDs) {
                    const schoolIds = this.securityService.getSchoolIdsFromPermissions(portal.RequiredPrivilegeIDs);
                    const allowedSchools = this.yearService.allDistrictSchools.filter(o => schoolIds.includes(o.Id));
                    this.studentSearchWithInjectedSchools(redirectUrl, portal, allowedSchools, false);
                }
                else {
                    this.studentSearchWithInjectedSchools(redirectUrl, portal, null, true);
                }
            }
        }
    }

    // TODO: WE SHOULD BE USING THE REQUIRESTUDENTGUARD. THIS IS DEPRECIATED.
    getStudentSearchSchools(redirectUrl: string, portal: Portal, permissionIds: number[]) {
        if (this.v10) {
            const studentSearchSchools: School[] = [];
            this.subscriptions.push(this.userService.getUserPermissionByPrivilegeIds(this.yearService.defaultYear.Id, 0, permissionIds).subscribe(permissions => {
                permissions.forEach(permission => {
                    if (permission.SiteId) {
                        const school = this.yearService.allDistrictSchools.find(s => s.Id == permission.SiteId);
                        if (school && !studentSearchSchools.includes(school)) {
                            studentSearchSchools.push(school);
                        }
                    }
                });
                this.studentSearchWithInjectedSchools(redirectUrl, portal, studentSearchSchools, false);
            }));
        }
        else {
            this.studentSearchWithInjectedSchools(redirectUrl, portal, null, false);
        }
    }

    // TODO: WE SHOULD BE USING THE REQUIRESTUDENTGUARD. THIS IS DEPRECIATED.
    studentSearchWithInjectedSchools(redirectUrl: string, portal: Portal, schools: School[], useLastSelectedSchool: boolean) {
        const dialogBox = this.dialog.open(
            StudentSearchComponent,
            {
                data: {
                    performRoute: true,
                    searchAndSelect: false,
                    redirectUrl: redirectUrl,
                    schools: schools,
                    useLastSelectedSchool: useLastSelectedSchool
                },
                width: '100%',
                height: '100%',
                maxHeight: '100vh',
                maxWidth: '100vw'
            }
        );
        dialogBox.afterClosed().subscribe(data => {
            if (data) {
                this.sharedService.currentPortal = portal;
            }
        });
    }

    StaffSearch(redirectUrl: string, portal: Portal) {
        const dialogBox = this.dialog.open(
            StaffSearchComponent,
            {
                data: {
                    performRoute: true,
                    searchAndSelect: false,
                    redirectUrl: redirectUrl
                },
                width: '100%',
                height: '100%',
                maxHeight: '100vh',
                maxWidth: '100vw'
            }
        );
        dialogBox.afterClosed().subscribe(data => {
            if (data) {
                this.sharedService.currentPortal = portal;
            }
        });
    }

    filterCheck(groupName: string) {
        if (this.favoritesFilter) {
            return this.menuByGroupName[groupName].filter(x => x.Selected).length > 0;
        }
        else {
            return true;
        }

    }

    savePreferences(portalMenu: PortalMenuItem) {
        // POST: saves the portal preferences
        portalMenu.Expanded = portalMenu.Expanded == 1 ? 0 : 1;
        const studentId = this.userService.userType == 2 && this.studentService.selectedStudent != null && this.studentService.selectedStudent.Id != null ? this.studentService.selectedStudent.Id : 0;
        const xmlStream: string[] = [];
        xmlStream.push(JSON.stringify(this.portalMenuPreferences));
        this.userPreferencesService.saveUserPreferences(studentId, '/OneSISPortalMenuPreferences', xmlStream, 0, '').subscribe();
        this.loaderService.isLoading.next(false);
    }

    showfavoritesMenu(groupName: string): boolean {
        if (this.favoritesFilter && this.menuByGroupName[groupName]) {
            let menus: Portal[] = [];

            if (this.v10) {
                menus = this.menuByGroupName[groupName].filter(x => (x.IsFavorite));
            }
            else {
                menus = this.menuByGroupName[groupName].filter(x => (x.Selected == true));
            }
            if (menus.length > 0) {
                return true;
            } else {
                return false;
            }
        }
        return false;
    }

    collapseExpandAll(expand: boolean) {
        const expanded = expand ? 1 : 0;
        if (this.v10) {
            this.portalMenuPreferences.PortalMenus.forEach(obj => {
                obj.Expanded = expanded;
            });
        }
        else {
            this.portals.forEach(obj => {
                obj.Expanded = expanded;
            });
        }
    }
}
